import { useDispatch, useSelector, useStore } from "react-redux"
import api from './botApi'
import {
    userMessageAppended,
    sessionsUpdated,
    sessionsListUpdated,
    currentUpdated,
    requestStarted,
    requestFinished,
    abandonPendingResponse,
    startStreamingResponse,
    appendStreamDelta,
    resetStreamBuffers,
    finalizeStreamingResponse,
    markStreamingFailed,
    sessionNameUpdated,
} from "./botSlice"
import { getCurrentConfig } from "../../config/currentBotConfig"
import { mapInteractionsToTranscript, mapSessionMetadata } from "./mapPlatformResponses"

const DESIGN_PREVIEW_LOCAL_SESSION = '__design_preview__'

/** Session ids with an in-flight background naming request. */
const namingInFlight = new Set()

function hasSessionName(name) {
    if (name == null) return false
    const normalizedName = String(name).trim()
    return normalizedName !== '' && normalizedName.toLowerCase() !== 'null'
}

function countCompletedModelTurns(transcript) {
    if (!Array.isArray(transcript)) return 0
    return transcript.filter(
        (entry) =>
            entry?.sender === 'MODEL' &&
            !entry.isStreaming &&
            !(entry.errorKey || entry.errorMessage)
    ).length
}

function formatTranscriptForNaming(transcript, texts = {}) {
    if (!Array.isArray(transcript)) return ''
    const userPrefix = texts.namingTranscriptUserPrefix || 'User:'
    const botPrefix = texts.namingTranscriptBotPrefix || 'Bot:'
    const lines = []
    for (const entry of transcript) {
        if (!entry?.text) continue
        if (entry.sender === 'USER') {
            lines.push(`${userPrefix} ${entry.text}`)
        } else if (entry.sender === 'MODEL' && !entry.isStreaming && !entry.errorKey && !entry.errorMessage) {
            lines.push(`${botPrefix} ${entry.text}`)
        }
    }
    return lines.join('\n')
}

/** Single sidebar row in admin design preview (no GET /sessions list). */
function buildDesignPreviewSessionStub(sessionId, config, texts = {}) {
    return {
        id: sessionId,
        botId: config?.botId,
        name: texts.designPreviewSessionName || '',
        lastUpdated: new Date().toISOString(),
    }
}

function isLocalDesignPreviewSession(sessionId) {
    return sessionId === DESIGN_PREVIEW_LOCAL_SESSION
}

function dispatchDesignPreviewLocalSession(config, dispatch, texts = {}) {
    const previewStub = buildDesignPreviewSessionStub(DESIGN_PREVIEW_LOCAL_SESSION, config, texts)
    dispatch(
        sessionsUpdated({
            sessions: [previewStub],
            currentSession: DESIGN_PREVIEW_LOCAL_SESSION,
            currentTranscript: [],
        })
    )
}

function resolveSessionIdFromNewSessionResponse(data) {
    if (!data || typeof data !== 'object') return null
    return data.id ?? data.sessionId ?? null
}

const useBotsService = () => {
    const botState = useSelector(state => state.bot)
    const dispatch = useDispatch()
    const reduxStore = useStore()
    const getTexts = () => reduxStore.getState().translate?.texts || {}

    const isActiveStream = (generation) =>
        reduxStore.getState().bot.streamGeneration === generation

    const syncSessions = async ({ restoreLast = true } = {}) => {
        const generation = reduxStore.getState().bot.streamGeneration
        try {
            const config = getCurrentConfig()
            if (!config) return []
            if (config.isDesignPreview) return []
            if (!config.agentId) throw new Error('agentId is required in bot config')

            const sessions = await api.getAllSessions(config.agentId)
            if (!isActiveStream(generation)) return []
            const previousSessions = reduxStore.getState().bot.sessions
            const mappedSessions = Array.isArray(sessions)
                ? sessions.map((session) => {
                    const mapped = mapSessionMetadata(session)
                    if (!hasSessionName(mapped.name)) {
                        const previous = previousSessions.find((s) => s.id === mapped.id)
                        if (hasSessionName(previous?.name)) {
                            mapped.name = previous.name
                        }
                    }
                    return mapped
                })
                : []

            if (mappedSessions.length === 0) return mappedSessions

            const sorted = sortSessions(mappedSessions)
            if (restoreLast) {
                const currentSession = sorted[0].id
                const interactions = await api.getSession(currentSession)
                if (!isActiveStream(generation)) return sorted
                dispatch(sessionsUpdated({
                    sessions: sorted,
                    currentSession,
                    currentTranscript: mapInteractionsToTranscript(interactions),
                }))
            } else {
                dispatch(sessionsListUpdated({ sessions: sorted }))
            }
            return mappedSessions
        } catch (e) {
            console.log(`couldn't fetch sessions: ${e}`)
            return []
        }
    }

    const resetDraft = () => {
        dispatch(abandonPendingResponse())
        dispatch(currentUpdated({ currentSession: null, currentTranscript: [] }))
    }

    const newSession = async ({ skipFetch = false, generation } = {}) => {
        const config = getCurrentConfig()
        const isStale = () => generation != null && !isActiveStream(generation)
        try {
            if (!config?.isDesignPreview && !config?.agentId) {
                throw new Error('agentId is required in bot config')
            }

            const data = await api.newSession()
            if (isStale()) return
            const sessionId = resolveSessionIdFromNewSessionResponse(data)

            if (config?.isDesignPreview) {
                if (sessionId) {
                    const previewStub = buildDesignPreviewSessionStub(sessionId, config, getTexts())
                    dispatch(
                        sessionsUpdated({
                            sessions: [previewStub],
                            currentSession: sessionId,
                            currentTranscript: [],
                        })
                    )
                } else {
                    console.warn("isDesignPreview: newSession response missing session id — using local preview session")
                    dispatchDesignPreviewLocalSession(config, dispatch, getTexts())
                }
                return
            }

            if (sessionId) {
                if (skipFetch) {
                    if (isStale()) return
                    dispatch(
                        sessionsUpdated({
                            sessions: reduxStore.getState().bot.sessions,
                            currentSession: sessionId,
                            currentTranscript: [],
                        })
                    )
                } else {
                    if (isStale()) return
                    await syncSessions()
                }
                return
            }

            if (isStale()) return
            await syncSessions()
        } catch (e) {
            console.log(`couldn't create session: ${e}`)
            if (config?.isDesignPreview && !isStale()) {
                dispatchDesignPreviewLocalSession(config, dispatch, getTexts())
            }
        }
    }

    const ensureSession = async (generation) => {
        if (generation != null && !isActiveStream(generation)) return null
        const existing = reduxStore.getState().bot.currentSession
        if (existing) return existing

        const config = getCurrentConfig()
        if (config?.isDesignPreview) {
            dispatchDesignPreviewLocalSession(config, dispatch, getTexts())
            return reduxStore.getState().bot.currentSession
        }

        await newSession({ skipFetch: true, generation })
        if (generation != null && !isActiveStream(generation)) return null
        return reduxStore.getState().bot.currentSession
    }

    const requestSessionName = async (chatSessionId, transcript) => {
        if (namingInFlight.has(chatSessionId)) return
        namingInFlight.add(chatSessionId)

        try {
            const transcriptText = formatTranscriptForNaming(transcript, getTexts())
            if (!transcriptText.trim()) return

            // Re-check: another path may have named the session meanwhile
            const sessionRow = reduxStore.getState().bot.sessions.find((s) => s.id === chatSessionId)
            if (hasSessionName(sessionRow?.name)) return

            const data = await api.updateSessionName(chatSessionId, transcriptText)
            const title = data?.name ?? data?.Name ?? null
            if (!hasSessionName(title)) return

            dispatch(sessionNameUpdated({ id: chatSessionId, name: String(title).trim() }))
        } catch (e) {
            console.warn(`couldn't name session: ${e}`)
        } finally {
            namingInFlight.delete(chatSessionId)
        }
    }

    const maybeRequestSessionName = (chatSessionId, transcript) => {
        try {
            const config = getCurrentConfig()
            if (config?.isDesignPreview) return

            const sessionId = chatSessionId || reduxStore.getState().bot.currentSession
            if (!sessionId || isLocalDesignPreviewSession(sessionId)) return

            const { sessions } = reduxStore.getState().bot
            const sessionRow = sessions.find((s) => s.id === sessionId)
            if (hasSessionName(sessionRow?.name)) return

            const transcriptForNaming = transcript || reduxStore.getState().bot.currentTranscript
            const modelTurns = countCompletedModelTurns(transcriptForNaming)
            if (modelTurns < 1) return

            void requestSessionName(sessionId, transcriptForNaming)
        } catch (e) {
            console.warn(`couldn't start session naming: ${e}`)
        }
    }
    
    const appendDiscoveryChoice = async (text) => {
        const trimmed = String(text ?? '').trim()
        if (!trimmed) return

        try {
            const cfg = getCurrentConfig()
            const welcome = cfg?.welcomeMessage?.trim()
            const { currentTranscript } = reduxStore.getState().bot

            const choice = { sender: "USER", text: trimmed, isDiscoveryChoice: true }
            const newTranscript = currentTranscript.length === 0 && welcome
                ? [{ sender: "MODEL", text: welcome }, choice]
                : [...currentTranscript, choice]
            dispatch(userMessageAppended({ currentTranscript: newTranscript }))
        } catch (e) {
            console.log(`couldn't append discovery choice: ${e}`)
        }
    }

    const removeLastDiscoveryChoice = () => {
        const { currentTranscript } = reduxStore.getState().bot
        if (!Array.isArray(currentTranscript) || currentTranscript.length === 0) return
        let lastIndex = -1
        for (let i = currentTranscript.length - 1; i >= 0; i -= 1) {
            if (currentTranscript[i]?.isDiscoveryChoice === true) {
                lastIndex = i
                break
            }
        }
        if (lastIndex === -1) return
        dispatch(userMessageAppended({
            currentTranscript: currentTranscript.filter((_, i) => i !== lastIndex),
        }))
    }

    const sendMessage = async (text, options = {}) => {
        const trimmed = String(text ?? '').trim()
        if (!trimmed) return
        const displayText = String(options.displayText ?? trimmed).trim() || trimmed
        const initialState = reduxStore.getState().bot
        if (initialState.requestInFlight) return
        const generation = initialState.streamGeneration

        dispatch(requestStarted(generation))

        try {
            const cfg = getCurrentConfig()
            const welcome = cfg?.welcomeMessage?.trim()
            const isDraft = !initialState.currentSession
            const draftTranscript = initialState.currentTranscript

            await ensureSession(generation)
            if (!isActiveStream(generation)) return

            const { currentSession, currentTranscript } = reduxStore.getState().bot

            if (!currentSession) {
                console.log("couldn't send message: no current session")
                return
            }

            const chatSessionId = currentSession

            let baseTranscript = isDraft ? draftTranscript : currentTranscript
            if (isDraft && baseTranscript.length === 0 && welcome) {
                baseTranscript = [{ sender: "MODEL", text: welcome }]
            }
            const newTranscript = [...baseTranscript, { sender: "USER", text: displayText }]
            dispatch(userMessageAppended({ currentTranscript: newTranscript }))

            if (cfg?.isDesignPreview && isLocalDesignPreviewSession(chatSessionId)) {
                dispatch(startStreamingResponse({ userText: displayText }))
                dispatch(finalizeStreamingResponse({
                    userText: trimmed,
                    modelMessage: getTexts().designPreviewStubReply || '',
                }))
                return
            }

            dispatch(startStreamingResponse({ userText: displayText }))

            let streamCompleted = false
            let streamSucceeded = false

            await api.sendMessageStream(trimmed, chatSessionId, {
                onEvent: (event) => {
                    if (!isActiveStream(generation)) return
                    const payload = event?.payload || {}
                    switch (event?.type) {
                        case 'thinking_delta':
                            dispatch(appendStreamDelta({
                                userText: displayText,
                                streamType: 'thinking_delta',
                                text: payload.text || payload.content || '',
                            }))
                            break
                        case 'message_delta':
                            dispatch(appendStreamDelta({
                                userText: displayText,
                                streamType: 'message_delta',
                                text: payload.text || payload.content || '',
                            }))
                            break
                        case 'reset':
                            dispatch(resetStreamBuffers({ userText: displayText }))
                            break
                        case 'done':
                            streamCompleted = true
                            streamSucceeded = true
                            dispatch(finalizeStreamingResponse({
                                userText: displayText,
                                modelMessage: payload.text ?? payload.modelMessage ?? payload.ModelMessage ?? null,
                            }))
                            break
                        case 'error':
                            streamCompleted = true
                            dispatch(markStreamingFailed({
                                userText: displayText,
                                errorCode: payload.errorCode,
                                source: payload.source,
                            }))
                            break
                        default:
                            break
                    }
                },
            })

            if (!isActiveStream(generation)) return

            if (!streamCompleted) {
                dispatch(markStreamingFailed({
                    userText: displayText,
                    errorCode: 'UNKNOWN_RUNTIME_ERROR',
                    source: 'Runtime',
                }))
            }

            if (streamSucceeded) {
                const namingTranscript = reduxStore.getState().bot.currentTranscript
                maybeRequestSessionName(chatSessionId, namingTranscript)
            }

            if (!cfg?.isDesignPreview && isActiveStream(generation)) {
                await syncSessions({ restoreLast: false })
            }
        } catch (e) {
            console.log(`couldn't send message: ${e}`)
            if (!isActiveStream(generation)) return
            dispatch(markStreamingFailed({
                userText: displayText,
                errorCode: 'UNKNOWN_RUNTIME_ERROR',
                source: 'Runtime',
            }))
        } finally {
            if (isActiveStream(generation)) {
                dispatch(requestFinished(generation))
            }
        }
    }
    
    const switchSession = async (id) => {
        dispatch(abandonPendingResponse())
        const generation = reduxStore.getState().bot.streamGeneration
        try {
            const interactions = await api.getSession(id)
            if (!isActiveStream(generation)) return
            dispatch(currentUpdated({
                currentSession: id,
                currentTranscript: mapInteractionsToTranscript(interactions),
            }))
        } catch (e) {
            console.log(`couldn't switch sessions: ${e}`)
        }
    }

    const getCurrentSession = () => {
        return botState.currentSession
    }

    const getAllSessions = () => {
        return botState.sessions
    }

    const getCurrentTranscript = () => {
        return botState.currentTranscript
    }

    const sortSessions = (sessions) => {
        let sorted = sessions.slice().sort((a, b) => {
            var keyA = new Date(a.lastUpdated)
            var keyB = new Date(b.lastUpdated)
            if (keyA < keyB) return 1;
            if (keyA > keyB) return -1;
            return 0;
          })
        return sorted
    }

    const isPendingResponse = () => {
        return botState.requestInFlight
    }

    return {
        syncSessions: syncSessions,
        resetDraft: resetDraft,
        newSession: newSession,
        ensureSession: ensureSession,
        sendMessage: sendMessage,
        appendDiscoveryChoice: appendDiscoveryChoice,
        removeLastDiscoveryChoice: removeLastDiscoveryChoice,
        switchSession: switchSession,
        getCurrentSession: getCurrentSession,
        getAllSessions: getAllSessions,
        getCurrentTranscript: getCurrentTranscript,
        isPendingResponse: isPendingResponse
    }
}

export default useBotsService
